import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { Card } from '@/components/ui/card';

interface PurchaseOrderFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  order?: any;
  onSuccess?: () => void;
}

interface OrderItem {
  id: string;
  description: string;
  quantity: number;
  unit_price: number;
  total: number;
}

export function PurchaseOrderFormDialog({ open, onOpenChange, order, onSuccess }: PurchaseOrderFormDialogProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [budgets, setBudgets] = useState<any[]>([]);
  const [nextNumber, setNextNumber] = useState<number | null>(null);
  const [items, setItems] = useState<OrderItem[]>([]);

  const form = useForm({
    defaultValues: {
      customer_po_reference: '',
      description: '',
      customer_id: '',
      project_id: '',
      budget_id: '',
      status: 'pending',
      total_amount: 0,
      order_date: new Date().toISOString().split('T')[0],
      delivery_date: '',
      notes: '',
    },
  });

  useEffect(() => {
    if (open) {
      fetchCustomers();
      fetchProjects();
      fetchBudgets();

      if (order) {
        form.reset({
          customer_po_reference: order.customer_po_reference || '',
          description: order.description || '',
          customer_id: order.customer_id || '',
          project_id: order.project_id || '',
          budget_id: order.budget_id || '',
          status: order.status || 'pending',
          total_amount: order.total_amount || 0,
          order_date: order.order_date || new Date().toISOString().split('T')[0],
          delivery_date: order.delivery_date || '',
          notes: order.notes || '',
        });
        setItems(order.items || []);
      } else {
        form.reset({
          customer_po_reference: '',
          description: '',
          customer_id: '',
          project_id: '',
          budget_id: '',
          status: 'pending',
          total_amount: 0,
          order_date: new Date().toISOString().split('T')[0],
          delivery_date: '',
          notes: '',
        });
        setItems([]);

        api.from('purchase_orders')
          .select('po_number')
          .order('po_number', { ascending: false })
          .limit(1)
          .then(({ data }) => {
            const maxNum = data?.[0]?.po_number || 0;
            setNextNumber(maxNum + 1);
          });
      }
    }
  }, [open, order, form]);

  const fetchCustomers = async () => {
    const { data } = await api
      .from('customers')
      .select('id,company_name,customer_number')
      .eq('is_active', true)
      .order('company_name');
    setCustomers(data || []);
  };

  const fetchProjects = async () => {
    const { data } = await api
      .from('projects')
      .select('id,name,project_number,customer_id')
      .eq('is_active', true)
      .order('project_number', { ascending: false });
    setProjects(data || []);
  };

  const fetchBudgets = async () => {
    const { data } = await api
      .from('budgets')
      .select('id,budget_number,title,total_amount,customer_id,project_id')
      .eq('is_active', true)
      .eq('status', 'approved')
      .order('budget_number', { ascending: false });
    setBudgets(data || []);
  };

  const watchCustomerId = form.watch('customer_id');
  const watchProjectId = form.watch('project_id');

  // Filter projects by customer
  const filteredProjects = watchCustomerId
    ? projects.filter(p => p.customer_id === watchCustomerId)
    : projects;

  // Filter budgets by customer and project
  const filteredBudgets = budgets.filter(b => {
    if (watchCustomerId && b.customer_id !== watchCustomerId) return false;
    if (watchProjectId && b.project_id !== watchProjectId) return false;
    return true;
  });

  // When budget is selected, load its data
  const handleBudgetSelect = (budgetId: string) => {
    form.setValue('budget_id', budgetId);
    const budget = budgets.find(b => b.id === budgetId);
    if (budget) {
      form.setValue('total_amount', budget.total_amount || 0);
      if (budget.customer_id && !watchCustomerId) {
        form.setValue('customer_id', budget.customer_id);
      }
      if (budget.project_id && !watchProjectId) {
        form.setValue('project_id', budget.project_id);
      }
    }
  };

  const calculateTotal = () => {
    const total = items.reduce((sum, item) => sum + (item.total || 0), 0);
    form.setValue('total_amount', total);
  };

  useEffect(() => {
    calculateTotal();
  }, [items]);

  const addItem = () => {
    setItems([
      ...items,
      {
        id: crypto.randomUUID(),
        description: '',
        quantity: 1,
        unit_price: 0,
        total: 0,
      },
    ]);
  };

  const updateItem = (id: string, field: keyof OrderItem, value: any) => {
    setItems(items.map(item => {
      if (item.id === id) {
        const updated = { ...item, [field]: value };
        if (field === 'quantity' || field === 'unit_price') {
          updated.total = (updated.quantity || 0) * (updated.unit_price || 0);
        }
        return updated;
      }
      return item;
    }));
  };

  const removeItem = (id: string) => {
    setItems(items.filter(item => item.id !== id));
  };

  const onSubmit = async (data: any) => {
    setLoading(true);
    try {
      const payload = {
        ...data,
        items: items,
        customer_id: data.customer_id || null,
        project_id: data.project_id || null,
        budget_id: data.budget_id || null,
        delivery_date: data.delivery_date || null,
        notes: data.notes || null,
      };

      if (order) {
        const { error } = await api
          .from('purchase_orders')
          .update(payload)
          .eq('id', order.id);

        if (error) throw error;

        toast({
          title: 'Pedido actualizado',
          description: 'El pedido se actualizó correctamente',
        });
      } else {
        const { error } = await api
          .from('purchase_orders')
          .insert([payload]);

        if (error) throw error;

        toast({
          title: 'Pedido creado',
          description: 'El pedido se creó correctamente',
        });
      }

      onOpenChange(false);
      onSuccess?.();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'No se pudo guardar el pedido',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-[95vw] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <DialogTitle>{order ? 'Editar Pedido' : 'Nuevo Pedido'}</DialogTitle>
            <Badge variant="outline" className="font-mono bg-primary/10">
              PO-{order ? String(order.po_number).padStart(4, '0') : nextNumber ? String(nextNumber).padStart(4, '0') : '....'}
            </Badge>
          </div>
          {!order && (
            <DialogDescription>
              Crea un nuevo pedido vinculado a un cliente, proyecto y presupuesto
            </DialogDescription>
          )}
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* Customer PO Reference */}
            <FormField
              control={form.control}
              name="customer_po_reference"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Referencia PO del Cliente</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Ej: PO-2026-001234" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Customer, Project, Budget */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="customer_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Cliente</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar cliente" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {customers.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            C-{String(c.customer_number).padStart(3, '0')} - {c.company_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="project_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Proyecto</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar proyecto" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {filteredProjects.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            P-{String(p.project_number).padStart(4, '0')} - {p.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="budget_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Presupuesto Aprobado</FormLabel>
                    <Select onValueChange={handleBudgetSelect} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Vincular presupuesto" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {filteredBudgets.map((b) => (
                          <SelectItem key={b.id} value={b.id}>
                            PRE-{String(b.budget_number).padStart(4, '0')} - {b.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />
            </div>

            {/* Status and Dates */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Estado</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="pending">Pendiente</SelectItem>
                        <SelectItem value="confirmed">Confirmado</SelectItem>
                        <SelectItem value="in_production">En Producción</SelectItem>
                        <SelectItem value="shipped">Enviado</SelectItem>
                        <SelectItem value="delivered">Entregado</SelectItem>
                        <SelectItem value="cancelled">Cancelado</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="order_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Fecha Pedido</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="delivery_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Fecha Entrega</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            {/* Description */}
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descripción</FormLabel>
                  <FormControl>
                    <Textarea {...field} placeholder="Descripción del pedido..." rows={2} />
                  </FormControl>
                </FormItem>
              )}
            />

            {/* Items */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">Líneas del Pedido</h3>
                <Button type="button" variant="outline" size="sm" onClick={addItem}>
                  <Plus className="w-4 h-4 mr-2" />
                  Añadir línea
                </Button>
              </div>

              {items.length === 0 ? (
                <Card className="p-6 text-center text-muted-foreground">
                  <p>No hay líneas. Puedes añadir manualmente o vincular un presupuesto aprobado.</p>
                </Card>
              ) : (
                <div className="space-y-2">
                  {items.map((item) => (
                    <Card key={item.id} className="p-3">
                      <div className="grid grid-cols-12 gap-2 items-center">
                        <div className="col-span-5">
                          <Input
                            placeholder="Descripción"
                            value={item.description}
                            onChange={(e) => updateItem(item.id, 'description', e.target.value)}
                          />
                        </div>
                        <div className="col-span-2">
                          <Input
                            type="number"
                            placeholder="Cant." className="min-w-[80px] text-right"
                            value={item.quantity}
                            onChange={(e) => updateItem(item.id, 'quantity', parseFloat(e.target.value) || 0)}
                          />
                        </div>
                        <div className="col-span-2">
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="Precio" className="min-w-[80px] text-right"
                            value={item.unit_price}
                            onChange={(e) => updateItem(item.id, 'unit_price', parseFloat(e.target.value) || 0)}
                          />
                        </div>
                        <div className="col-span-2 text-right font-semibold">
                          {item.total.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
                        </div>
                        <div className="col-span-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="text-destructive"
                            onClick={() => removeItem(item.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* Total */}
            <Card className="p-4 bg-muted/30">
              <div className="flex justify-between items-center">
                <span className="font-semibold text-lg">Total:</span>
                <span className="font-bold text-2xl text-primary">
                  {form.watch('total_amount').toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
                </span>
              </div>
            </Card>

            {/* Notes */}
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notas</FormLabel>
                  <FormControl>
                    <Textarea {...field} placeholder="Notas adicionales..." rows={2} />
                  </FormControl>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
                Cancelar
              </Button>
              <Button type="submit" disabled={loading}>
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {order ? 'Actualizar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
